import React, { useState, useEffect } from 'react';
import Header from './Header';
import HowToPlayButton from '../HowToPlay/HowToPlayButton';
import ErrorPanel from '../Common/ErrorPanel';
import LanguageSelectionModal from '../LanguageSelection/LanguageSelection';
import { useLanguage } from '../../hooks/useLanguage';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import LanguageModalContext from '../../contexts/LanguageModalContext';
import Footer from '../Common/Footer';

interface GamePageLayoutProps {
  children: React.ReactNode;
  title?: string;
  isHeroTitle?: boolean;
  error?: string | null;
  loading?: boolean;
  onMenuClick?: () => void;
  onSettingsClick?: () => void;
  showSettings?: boolean;
  showFooter?: boolean;
  showHowToPlay?: boolean;
  wide?: boolean;
}

const GamePageLayout: React.FC<GamePageLayoutProps> = ({
  children,
  title,
  isHeroTitle=false,
  error,
  loading = false,
  onMenuClick,
  onSettingsClick,
  showSettings = false,
  showFooter = false,
  showHowToPlay=true,
  wide=false
}) => {
  const [, setLanguage] = useLanguage();
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [userHasSelectedLanguage, setUserHasSelectedLanguage] = useLocalStorage('userHasSelectedLanguage', '');

  // Show modal if user hasn't explicitly selected a language before
  useEffect(() => {
    if (!userHasSelectedLanguage) {
      setIsLanguageModalOpen(true);
    }
  }, [userHasSelectedLanguage]);

  const handleLanguageSelect = (languageCode: string) => {
    setLanguage(languageCode);
    setUserHasSelectedLanguage('true');
    setIsLanguageModalOpen(false);
  };

  const openLanguageModal = () => {
    setIsLanguageModalOpen(true);
  };

  const contextValue = {
    openLanguageModal
  };

  return (
    <LanguageModalContext.Provider value={contextValue}>
      <div className="h-screen flex flex-col overflow-hidden bg-gradient-to-b from-sky-300 via-purple-200 to-yellow-200">
        <Header
          onMenuClick={onMenuClick}
          onSettingsClick={onSettingsClick}
          showSettings={showSettings}
        />
        <main className="flex-1 flex flex-col justify-between gap-6 p-3 sm:p-5 overflow-auto">
          {error ? (
            <div className="flex-1 flex items-center justify-center">
              <ErrorPanel error={error} showBackToHome={true} />
            </div>
          ) : (
            <>
              {title && isHeroTitle && (
                <div className="flex-shrink-0 text-center">
                  <h1 className="text-5xl font-black text-white drop-shadow-lg leading-tight">
                    {title}
                  </h1>
                </div>
              )}
              {title && !isHeroTitle && (
                <div className="flex-shrink-0 text-center">
                  <h1 className="text-3xl font-black text-white drop-shadow-lg leading-tight">
                    {title}
                  </h1>
                </div>
              )}
              
              <div className={`flex-1 flex items-center justify-center mx-auto ${wide ? "" : "sm:w-[440px]"}`}>
                {children}
              </div>
              
              <div className="w-full flex-shrink-0 flex flex-col justify-center items-center gap-2">
                {showHowToPlay && <HowToPlayButton />}
                {showFooter && <Footer />}
              </div>
            </>
          )}
        </main>
        
        <LanguageSelectionModal 
          isOpen={isLanguageModalOpen} 
          onLanguageSelect={handleLanguageSelect} 
        />
      </div>
    </LanguageModalContext.Provider>
  );
};

export default GamePageLayout;